import React, { useState } from "react";
import { Trash2, Upload, FileText, Eye } from "react-feather";
import toast from "react-hot-toast";
import AttachmentPreviewModal from "../../../../components/common/AttachmentPreviewModal";
import InlineFilePreview from "../../components/InlineFilePreview";
import {
  useUploadPettyCashRefillAttachmentMutation,
  useDeletePettyCashRefillAttachmentMutation,
  useGetPettyCashRefillAttachmentContentQuery,
} from "../../pettyCashApi";

// One supporting document per refill request (Batch195, 2026-09-22) - the same
// upload / view (blob) / remove widget the voucher receipt uses (ReceiptUpload),
// on the refill endpoints. 2MB, JPEG/PNG/PDF, only while awaiting approval.
//
// Since 2026-10-13 the document is shown inline as soon as the refill opens, like
// the voucher receipt ("refill view to be automatic like voucher view"). Clicking
// the preview opens the full-size modal.
const RefillAttachment = ({ recordGuid, hasAttachment, fileName, canManage }) => {
  const [uploadAttachment, { isLoading: isUploading }] = useUploadPettyCashRefillAttachmentMutation();
  const [deleteAttachment] = useDeletePettyCashRefillAttachmentMutation();
  const { data: blob, isFetching, isError } = useGetPettyCashRefillAttachmentContentQuery(recordGuid, {
    skip: !hasAttachment,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleUpload = async () => {
    if (!selectedFile) return toast.error("Choose a file first");
    if (selectedFile.size > 2 * 1024 * 1024) return toast.error("File must be 2MB or smaller");
    try {
      await uploadAttachment({ recordGuid, file: selectedFile }).unwrap();
      toast.success("Document attached");
      setSelectedFile(null);
    } catch (error) {
      toast.error(error?.message || "Could not attach the document");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await deleteAttachment(recordGuid).unwrap();
      toast.success("Document removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the document");
    }
  };

  return (
    <div>
      <label className="form-label">Supporting document</label>
      {hasAttachment ? (
        <div className="d-flex align-items-start gap-3 flex-wrap">
          <InlineFilePreview
            blob={blob}
            fileName={fileName}
            loading={isFetching || !isError ? undefined : false}
            onOpen={blob ? () => setShowPreview(true) : undefined}
          />
          <div className="d-flex align-items-center gap-2">
            <FileText size={16} className="text-muted" />
            <span>{fileName || "Document"}</span>
            <button type="button" className="btn btn-link p-1" onClick={() => setShowPreview(true)} disabled={!blob} title="Open full size">
              <Eye size={16} />
            </button>
            {canManage && (
              <button type="button" className="btn btn-link p-1 text-danger" onClick={handleDelete} title="Remove">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      ) : (
        canManage && (
          <div className="d-flex gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="form-control"
              style={{ maxWidth: 280 }}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            <button type="button" className="btn btn-outline-primary" onClick={handleUpload} disabled={isUploading || !selectedFile}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )
      )}
      {!hasAttachment && !canManage && <div className="text-muted small">No document attached.</div>}

      <AttachmentPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        blob={blob}
        fileName={fileName}
        title="Supporting document"
      />
    </div>
  );
};

export default RefillAttachment;
